import React, { useState } from 'react';
import classNames from 'classnames';
import { Button } from 'components/Button';
import ProgressBar from './ProgressBar/ProgressBar';
import * as Collapsible from '@radix-ui/react-collapsible';
import styles from './Program.module.scss';
import { PlayIcon } from './PlayIcon/PlayIcon';
import { useTranslation } from 'hooks/useTranslation';
import { Card } from '@yleisradio/areena-types';
import { findLabel } from 'utils/card';
import { Marker } from './markers';
import ChevronRight from 'assets/chevronRight.svg';
import {
  ProgramControl,
  DisabledProgramControl,
} from './ProgramControl/ProgramControl';
import { determineBroadcastStatus } from '../utils';
import { getControlKey } from 'utils/control';

interface ProgramProps {
  program: Card;
  currentTime: Date;
}

export const Program = ({ program, currentTime }: ProgramProps) => {
  const [infoOpen, setInfoOpen] = useState(false);
  const { title, description = '', labels = [], pointer } = program;
  const t = useTranslation();

  const getControls = () => {
    const controls = [];

    if (pointer) {
      controls.push(
        <Button
          key="program-page"
          text={t('linkToProgramPage')}
          iconRight={ChevronRight}
          pointer={pointer}
          variant="text"
          padding="none"
        />
      );
    }

    if (program.controls) {
      controls.push(
        ...program.controls.map((control) => {
          return (
            <ProgramControl key={getControlKey(control)} control={control} />
          );
        })
      );
    }

    if (getOndemandStatus() === 'upcoming') {
      const tKey =
        findLabel(labels, 'mediaType')?.raw === 'audio'
          ? 'programOndemandAudioUpcoming'
          : 'programOndemandVideoUpcoming';

      controls.push(
        <DisabledProgramControl key="ondemand-upcoming" label={t(tKey)} />
      );
    }

    if (getOndemandStatus() === 'norights') {
      const tKey =
        findLabel(labels, 'mediaType')?.raw === 'audio'
          ? 'programAudioNoRights'
          : 'programVideoNoRights';

      controls.push(
        <em key="ondemand-norights" className={styles.noRights}>
          {t(tKey)}
        </em>
      );
    }

    return controls.length > 0 ? (
      <div className={styles.controlsContainer}>{controls}</div>
    ) : null;
  };

  const timeElement = () => {
    const startDateLabel = findLabel(labels, 'broadcastStartDate');

    return startDateLabel && startDateLabel.raw && startDateLabel.formatted ? (
      <time dateTime={startDateLabel.raw} className={styles.programTime}>
        {startDateLabel.formatted}
      </time>
    ) : null;
  };

  const areenaStatusIconElement = () => {
    const ondemandStatus = getOndemandStatus();

    if (ondemandStatus === 'current' || ondemandStatus === 'upcoming') {
      return (
        <PlayIcon
          aria-label={
            ondemandStatus === 'current'
              ? t('availableInAreena')
              : t('upcomingInAreena')
          }
          active={ondemandStatus === 'current'}
        />
      );
    }

    return null;
  };

  const videoFormatElement = () => {
    const videoFormat = findLabel(labels, 'videoFormat');

    if (videoFormat) return <Marker>{videoFormat.formatted}</Marker>;
    return null;
  };

  const highlightElement = () => {
    const highlight = findLabel(labels, 'highlight');

    if (highlight) {
      return <Marker>{highlight.formatted}</Marker>;
    }

    return null;
  };

  const hardOfHearingSubtitlingElement = () => {
    const subtitling = findLabel(labels, 'hardOfHearingSubtitling');

    if (subtitling)
      return <span className={styles.infoMarker}>{subtitling.formatted}</span>;
    return null;
  };

  const durationElement = () => {
    const duration = findLabel(labels, 'duration');

    if (duration)
      return <span className={styles.infoMarker}>{duration.formatted}</span>;
    return null;
  };

  const handleClick = () => setInfoOpen(!infoOpen);

  const getProgramStartDate = () =>
    findLabel(labels, 'broadcastStartDate')?.raw;
  const getProgramEndDate = () => findLabel(labels, 'broadcastEndDate')?.raw;
  const getOndemandStatus = () => findLabel(labels, 'ondemandStatus')?.raw;

  return (
    <Collapsible.Root
      role="group"
      className={classNames(
        styles.programRoot,
        (determineBroadcastStatus(program, currentTime) === 'current' ||
          infoOpen) &&
          styles.programActive
      )}
      open={infoOpen}
    >
      <header>
        <Collapsible.Trigger asChild>
          <button
            className={classNames(
              styles.programHeader,
              (determineBroadcastStatus(program, currentTime) === 'current' ||
                infoOpen) &&
                styles.programHeaderActive
            )}
            onClick={handleClick}
          >
            <div className={styles.programTitleRow}>
              {timeElement()}
              {areenaStatusIconElement()}
              <div>
                <h3 className={styles.programTitle}>{title}</h3>
                {highlightElement()}
              </div>
            </div>
            {determineBroadcastStatus(program, currentTime) === 'current' && (
              <ProgressBar
                start={Date.parse(getProgramStartDate() || '')}
                end={Date.parse(getProgramEndDate() || '')}
                current={() => currentTime.getTime()}
              />
            )}
          </button>
        </Collapsible.Trigger>
      </header>

      <Collapsible.Content className={styles.programInfo}>
        {description} <span>{hardOfHearingSubtitlingElement()}</span>{' '}
        <span>{durationElement()}</span> <span>{videoFormatElement()}</span>
        {getControls()}
      </Collapsible.Content>
    </Collapsible.Root>
  );
};
